import { useState, useEffect } from 'react'
import { apiClient } from '../utils/api'

export interface FamilyFeature {
  feature_key: string
  enabled: boolean
  display_name: string
  description?: string
  category?: string
}

interface UseFamilyFeaturesReturn {
  features: Record<string, FamilyFeature>
  loading: boolean
  error: string | null
  isFeatureEnabled: (featureKey: string) => boolean
  isChoreVerificationEnabled: boolean
  refetch: () => Promise<void>
}

export function useFamilyFeatures(): UseFamilyFeaturesReturn {
  const [features, setFeatures] = useState<Record<string, FamilyFeature>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFeatures = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/api/family/features')

      if (response.success && response.data) {
        const featuresMap: Record<string, FamilyFeature> = {}
        response.data.forEach((feature: FamilyFeature) => {
          featuresMap[feature.feature_key] = feature
        })
        setFeatures(featuresMap)
      } else {
        setError('Failed to load family features')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      console.error('Error fetching family features:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFeatures()
  }, [])

  const isFeatureEnabled = (featureKey: string): boolean => {
    return features[featureKey]?.enabled ?? false
  }

  // Convenience property for chore verification check
  // Defaults to true (verification required) if feature not loaded yet
  const isChoreVerificationEnabled = features['chore_verification']?.enabled ?? true

  return {
    features,
    loading,
    error,
    isFeatureEnabled,
    isChoreVerificationEnabled,
    refetch: fetchFeatures
  }
}
